import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  InputAdornment,
  Skeleton,
} from '@mui/material';
import Header from '../Header';
import Footer from '../Footer';
import { API_URL, BASE_URL } from '../Config';
import { useParams } from 'react-router-dom';
import config from "../common/Config";
import { Iconify } from "src/components/iconify";
import "../common/commonStyles.css";

const ViewShortQty = () => {
  const { picklistNo } = useParams();
  const [newSales, setNewSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const accessToken = localStorage.getItem('accessToken');

  const columns = [
    { id: "code", label: "Code", width: 120 },
    { id: "name", label: "Product", width: 250 },
    { id: "order_qty", label: "Order Qty", width: 120, numeric: true },
    { id: "pick_qty", label: "Pick Qty", width: 120, numeric: true },
    { id: "action", label: "Action", width: 100 },
  ];

  const fetchData = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    const token = localStorage.getItem("token");

    setLoading(true);
    const formData = new FormData();
    formData.append('searchByFromdate', '');
    formData.append('searchByTodate', '');
    formData.append('draw', '1');
    formData.append('start', '0');
    formData.append('length', '50');
    formData.append('role', '');
    formData.append('id', picklistNo);

    const apiUrl = `${API_URL}/get_short_list`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch short qty data');
      }

      const data = await response.json();
      
      if (data && data.data !== null && data.data !== undefined) {
        if (Array.isArray(data.data)) {
          setNewSales(data.data);
        } else {
          setNewSales([data.data]);
        }
      } else if (Array.isArray(data)) {
        setNewSales(data);
      } else {
        setNewSales([]);
      }
    } catch (error) {
      console.error('Error fetching short qty data:', error);
      setNewSales([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (picklistNo) {
      fetchData();
    }
  }, [picklistNo]);

  const filteredSales = useMemo(() => {
    if (!searchTerm.trim()) {
      return newSales;
    }
    const searchLower = searchTerm.toLowerCase().trim();
    return newSales.filter((item) => {
      return (
        (item.code && String(item.code).toLowerCase().includes(searchLower)) ||
        (item.name && item.name.toLowerCase().includes(searchLower)) ||
        (item.order_qty && String(item.order_qty).includes(searchLower)) ||
        (item.pick_qty && String(item.pick_qty).includes(searchLower))
      );
    });
  }, [newSales, searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedSales = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredSales.slice(startIndex, endIndex);
  }, [filteredSales, page, rowsPerPage]);

  const renderCell = (columnId, row) => {
    if (columnId === "action") {
      return (
        <IconButton
          size="small"
          href={`${BASE_URL}/picking/confirm_short_qty/${row.picklist_number}/${row.product_id}/${row.order_type}`}
          target="_blank"
   
          title="View"
        >
          <Iconify icon="eva:eye-fill" width={18} height={18} />
        </IconButton>
      );
    }
    return row[columnId] ?? "";
  };

  return (
    <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2}}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography sx={{ fontWeight: 500, color: "#212b36", fontSize: "1.125rem" }}>
                  View Short Qty - <Box sx={{ fontWeight: 400 }} component="span"> Picklist #{picklistNo}</Box>
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  className="input-border"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  placeholder="Search products..."
                  variant="outlined"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                        </InputAdornment>
                      )
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table
              size="small"
              className="table-cell-border"
            >
              <colgroup>
                {columns.map((column) => (
                  <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow className="table-cell-header">
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                      sx={{
                        width: column.width ? `${column.width}px` : "auto"
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                          sx={{ py: 1, fontSize: "0.75rem", width: column.width ? `${column.width}px` : "auto" }}
                        >
                          <Skeleton variant="text" width="60%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginatedSales.length > 0 ? (
                  paginatedSales.map((row, rowIndex) => (
                    <TableRow
                      key={row.product_id || row.code || rowIndex}
                      hover
                      className="table-cell-style"
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                          sx={{
                            width: column.width ? `${column.width}px` : "auto",
                            ...(column.numeric && { fontWeight: 500 })
                          }}
                        >
                          {renderCell(column.id, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ py: 8 }}>
                      <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <TablePagination
              component="div"
              count={filteredSales.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="pagination-styles"
            />
          </Box>
        </Paper>
    </Box>
  );
};

export default ViewShortQty;
